import {
  readNullableOneOf,
  readNullableString,
  readOneOf,
  readRecord,
  readRecords,
  readString,
} from '@/lib/readers';
import type { LiveRow } from '@/lib/pendingWrites';

export interface ProductImage {
  objectName: string;
  contentType: string;
  sourceUrl: string;
}

export const VERSION_KINDS = ['manual', 'import'] as const;
export type VersionKind = (typeof VERSION_KINDS)[number];

/** Who wrote one version of a product, when, and what they wrote it with. */
export interface VersionMetadata {
  kind: VersionKind;
  actor: string;
  changedAt: string | null;
  importId: string | null;
  fileName: string | null;
}

export const PRODUCT_FIELDS = [
  'name',
  'description',
  'dimension',
  'price',
  'category',
  'product_images',
  'mood_images',
] as const;
export type ProductField = (typeof PRODUCT_FIELDS)[number];

/** One field one version changed, and what it held before. */
export interface FieldChange {
  field: string;
  /** The field as a known product field, or null when the app does not know it. */
  knownField: ProductField | null;
  previous: unknown;
  current: unknown;
}

/** The whole content of one product at one point, and what changed to produce it. */
export interface ProductVersion {
  id: string;
  name: string;
  description: string;
  dimension: string;
  price: string;
  category: string;
  productImages: ProductImage[];
  moodImages: ProductImage[];
  changes: FieldChange[];
  metadata: VersionMetadata;
}

/** The document id is `sku_market` in lower case, or the sku alone if there is no market.
 *  All the content is in the versions, and `current` is the version the app shows. */
export interface Product extends LiveRow {
  id: string;
  sku: string;
  market: string;
  brand: string;
  currentVersionId: string;
  /** Oldest first. The last is not always the one presented. */
  versions: ProductVersion[];
  current: ProductVersion;
}

function images(value: unknown): ProductImage[] {
  return readRecords(value)
    .filter((item) => typeof item.object_name === 'string' && item.object_name)
    .map((item) => ({
      objectName: readString(item.object_name),
      contentType: readString(item.content_type),
      sourceUrl: readString(item.source_url),
    }));
}

/** Returns the id a product is stored under: `{sku}_{market}` lowercased, or the sku alone. */
export function toProductId(sku: string, market: string): string {
  return market ? `${sku}_${market.toLowerCase()}` : sku;
}

/** Returns the number in a version id, or 0 if the id has no number. */
export function versionNumber(versionId: string): number {
  const digits = versionId.replace(/^v/, '');
  return /^\d+$/.test(digits) ? Number(digits) : 0;
}

function metadata(value: unknown): VersionMetadata {
  const raw = readRecord(value);
  return {
    kind: readOneOf(VERSION_KINDS, raw.kind, 'manual'),
    actor: readString(raw.actor),
    changedAt: readNullableString(raw.changed_at),
    importId: readNullableString(raw.import_id),
    fileName: readNullableString(raw.file_name),
  };
}

function changes(value: unknown): FieldChange[] {
  return readRecords(value).map((raw) => ({
    field: readString(raw.field),
    knownField: readNullableOneOf(PRODUCT_FIELDS, raw.field),
    previous: raw.previous,
    current: raw.current,
  }));
}

/** Reads one version off a product, named by the key it is stored under. */
export function parseProductVersion(versionId: string, raw: Record<string, unknown>): ProductVersion {
  return {
    id: versionId,
    name: readString(raw.name),
    description: readString(raw.description),
    dimension: readString(raw.dimension),
    price: readString(raw.price),
    category: readString(raw.category),
    productImages: images(raw.product_images),
    moodImages: images(raw.mood_images),
    changes: changes(raw.changes),
    metadata: metadata(raw.metadata),
  };
}

function versions(value: unknown): ProductVersion[] {
  return Object.entries(readRecord(value))
    .map(([versionId, raw]) => parseProductVersion(versionId, readRecord(raw)))
    .sort((one, other) => versionNumber(one.id) - versionNumber(other.id));
}

/** Returns one product at its current version, or null when that version is missing or unnamed. */
export function parseProduct(documentId: string, raw: Record<string, unknown>): Product | null {
  const history = versions(raw.versions);
  const currentVersionId = readString(raw.current_version_id);
  const current = history.find((one) => one.id === currentVersionId);
  if (!documentId || !current || !current.name) return null;
  return {
    id: documentId,
    sku: readString(raw.sku) || documentId,
    market: readString(raw.market).toUpperCase(),
    brand: readString(raw.brand),
    currentVersionId,
    versions: history,
    current,
  };
}
